import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import EllipsisLoader from "../components/EllipsisLoader";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { IS_DEV } from "../resources/constants";
import { scrollToTop } from "../util";

const LoginScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const windowSize = useWindowSize();

  const [isFormValid, setIsFormValid] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    scrollToTop();
    navContext.resetNextBtn();
    navContext.resetOnNavigateBack();
    navContext.setNextBtnText("Login");
    navContext.setNextButtonEnabled(false);
    navContext.setRestartButtonVisible(true);
    navContext.setBackButtonVisible(true);
    navContext.setSubtotalVisible(false);
    navContext.setLoginButtonVisible(false);
    navContext.setFooterNavVisible(true);
    navContext.setNextBtnOnClick(submitLoginForm);
  }, []);

  useEffect(() => {
    // Need to re-run this whenever passwordConfirm changes otherwise
    // the most up-to-date value won't be used
    navContext.setNextBtnOnClick(submitLoginForm);
  }, [email, password]);

  useEffect(() => {
    if (windowSize.width > 768) {
      navContext.setNextButtonVisible(false);
    } else {
      navContext.setNextButtonVisible(true);
    }
  }, [windowSize.width]);

  useEffect(() => {
    if (apiContext.isLoading) {
      navContext.setNextButtonEnabled(false);
      setIsFormValid(false);
      return;
    }
    if (IS_DEV) {
      navContext.setNextButtonEnabled(true);
      setIsFormValid(true);
      return;
    }
    if (
      email.trim() !== "" &&
      /\S+@\S+\.\S+/.test(email) && // Email regex
      password !== ""
    ) {
      navContext.setNextButtonEnabled(true);
      setIsFormValid(true);
    } else {
      navContext.setNextButtonEnabled(false);
      setIsFormValid(false);
    }
  }, [email, password, apiContext.isLoading]);

  const submitLoginForm = async () => {
    setEmailError("");
    setPasswordError("");
    try {
      const result = await apiContext.loginUser({
        email: email,
        password: password,
      });

      if (result.errors) {
        if (result.errors.email) {
          setEmailError(result.errors.email[0]);
        }
        if (result.errors.password) {
          setPasswordError(result.errors.password[0]);
        }
      } else if (result.error) {
        setEmailError(result.error);
      } else if (result.user) {
        userContext.setEmail(result.user.email);
        userContext.setFirstName(result.user.name);
        userContext.setLastName(result.user.surname);
        userContext.setPhone(result.user.phone);
        userContext.setEmail(result.user.email);
        userContext.setIsLoggedIn(true);
        navContext.goNext();
      } else {
        alert("An unknown error occurred. Please try again.");
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const onClickNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await submitLoginForm();
  };

  return (
    <div className={"screen screen-sign-up container"}>
      <h2 className="h1">Login to continue.</h2>
      <p className={"subtitle"} />
      <form className={"sign-up-form"}>
        <div className={"form-group"}>
          <div className={"input-wrapper"}>
            <TextInput
              id={"email"}
              placeholder={"Email*"}
              value={email}
              onChange={(value) => setEmail(value)}
              onScreenKeyboard={userContext.isKioskMode}
              className={emailError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {emailError !== "" && <p className={"error-msg"}>{emailError}</p>}
          </div>
          <div className={"input-wrapper"}>
            <TextInput
              id={"password"}
              placeholder={"Password*"}
              value={password}
              onChange={(value) => setPassword(value)}
              onScreenKeyboard={userContext.isKioskMode}
              type={"password"}
              className={passwordError !== "" ? "error" : ""}
              disabled={apiContext.isLoading}
            />
            {passwordError !== "" && (
              <p className={"error-msg"}>{passwordError}</p>
            )}
          </div>
        </div>
        <div className={"form-group form-footer"}>
          <div />
          <button
            disabled={!isFormValid}
            className={"btn submit-btn"}
            onClick={onClickNext}
          >
            Login
          </button>
        </div>
      </form>
      {apiContext.isLoading && (
        <div className={"fullscreen-loader"}>
          <EllipsisLoader />
        </div>
      )}
    </div>
  );
});

export default LoginScreen;
